import { Observable, Subject } from 'rxjs';
import { CoreEnvironment } from '../data/core-environment';
import { NativeDeferred, NativeQ } from '../data/native-q';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { SmeWebTelemetry } from '../diagnostics/sme-web-telemetry';
import { Strings } from '../generated/strings';
import { EnvironmentModuleEntryPoint } from '../manifest/environment-modules';
import {
    CommandCallBackType,
    RpcActivateDataInternal,
    RpcBase,
    RpcBaseData,
    RpcDeactivateResult,
    RpcInboundCommands,
    RpcInboundHandlers,
    RpcInitData,
    RpcInitDataInternal,
    RpcInitResult,
    RpcMode,
    RpcOpenDataInternal,
    RpcOpenResult,
    RpcOutboundCommands,
    RpcOutboundHandlers,
    RpcPingResult,
    RpcShutdownData,
    RpcShutdownResult,
    RpcType,
    rpcVersion
} from './rpc-base';
import { RpcManager } from './rpc-manager';
import { RpcOutbound } from './rpc-outbound';
import { RpcSeek, RpcSeekMode, RpcSeekResult } from './seek/rpc-seek-model';

/**
 * Deferred data object.
 */
export interface DeferredData<TData, TResult> {
    deferred: NativeDeferred<TResult>;
    data: TData;
}

/**
 * The Rpc class.
 */
export class Rpc {
    // RPC timeout 10 seconds.
    private static rpcTimeout = 10 * 1000;

    /**
     * This subject is updated whenever there's new reported data
     */
    private rpcSubjects: { [index: string]: Subject<DeferredData<any, any>> };

    /**
     * Deferred response collection.
     */
    private deferredCollection = new Map<RpcOutboundCommands, DeferredData<any, any>>();

    /**
     * Active status of rpc by Observable.
     */
    private stateChangedInternal: Subject<boolean> = new Subject<boolean>();

    /**
     * Active status of rpc.
     */
    private stateActiveInternal = false;

    /**
     * Inbound module handlers to process when rpc is called.
     *  - called from Module to Shell.
     */
    private rpcInboundHandlers: RpcInboundHandlers = {
        FailedHandler: (data: RpcBaseData) => {
            return this.processNextForSubject<RpcBaseData, void>(RpcInboundCommands.Failed, data);
        },
        SeekHandler: (data: RpcSeek) => {
            return Promise.resolve(<RpcSeekResult>{ name: data.sourceName, subName: data.sourceSubName });
        }
    };

    /**
     * Outbound shell handlers to process when rpc is called.
     *  - called from Shell to Module.
     *  - if code reached a handler, module is not ready yet.
     *    set timeout for RPC call.
     */
    private rpcOutboundHandlers: RpcOutboundHandlers = {
        InitHandler: (data: RpcInitDataInternal) =>
            this.createTimerPromise<RpcInitDataInternal, RpcInitResult>(
                RpcOutboundCommands.Init, Rpc.rpcTimeout,
                <RpcInitDataInternal>{ locale: data.locale }),
        OpenHandler: (rpcOpenData: RpcOpenDataInternal) =>
            this.createTimerPromise<RpcOpenDataInternal, RpcOpenResult>(RpcOutboundCommands.Open, Rpc.rpcTimeout, rpcOpenData),
        ActivateHandler: (data: RpcActivateDataInternal) =>
            this.createTimerPromise<RpcBaseData, void>(RpcOutboundCommands.Activate, Rpc.rpcTimeout, data),
        Deactivate2Handler: (data: RpcBaseData) =>
            this.createTimerPromise<RpcBaseData, RpcDeactivateResult>(RpcOutboundCommands.Deactivate2, Rpc.rpcTimeout, data),
        ShutdownHandler: (data: RpcShutdownData) =>
            this.createTimerPromise<RpcShutdownData, RpcShutdownResult>(RpcOutboundCommands.Shutdown, Rpc.rpcTimeout, data),
        PingHandler: () => {
            this.changeActiveState(true);
            return Promise.resolve(<RpcPingResult>{ name: 'pong' });
        }
    };

    /**
     * Rpc manager object.
     */
    public rpcManager: RpcManager = new RpcManager();

    /**
     * Initializes a new instance of the Rpc class.
     *
     * @param http the Http class instance injected.
     */
    constructor() {
        this.rpcSubjects = {};
        const commands = Object.keys(RpcInboundCommands);
        commands.forEach((value) => {
            this.rpcSubjects[RpcInboundCommands[value]] = new Subject<any>();
        });
    }

    /**
     * Gets observable to watch the state change.
     */
    public get stateChanged(): Observable<boolean> {
        this.stateChangedInternal.next(this.stateActiveInternal);
        return this.stateChangedInternal.asObservable();
    }

    /**
     * Gets the state of rpc.
     */
    public get stateActive(): boolean {
        return this.stateActiveInternal;
    }

    /**
     * Gets whether rpc is running on the shell.
     */
    public get isShell(): boolean {
        if (this.rpcManager.rpcChannel == null) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.RpcNotInitialized.message;
            throw new Error(message);
        }

        return this.rpcManager.rpcChannel.rpcMode === RpcMode.Shell;
    }

    /**
     * Register inbound command handler.
     *
     * @param command The command name.
     * @param handler The command handler.
     */
    public registerInboundHandler(command: string, handler: CommandCallBackType): void {
        const handlerName = RpcBase.commandToHandlerName(command);
        this.rpcInboundHandlers[handlerName] = handler;
        this.rpcManager.registerInboundHandler(command, handler);
    }

    /**
     * Register outbound command handler.
     *
     * @param command The command name.
     * @param handler The command handler.
     */
    public registerOutboundHandler(command: string, handler: CommandCallBackType): void {
        const handlerName = RpcBase.commandToHandlerName(command);
        this.rpcOutboundHandlers[handlerName] = handler;
    }

    /**
     * Initializes Rpc configuration
     */
    public init(): void {
        this.rpcManager.init(this.rpcInboundHandlers, this.rpcOutboundHandlers);
        if (this.isShell) {
            this.changeActiveState(true);
        }
    }

    /***************************************************************
     * Section for Shell usage.
     ***************************************************************/

    /**
     * This updates its value every time there's a reported data from the rpc channel
     */
    public moduleSubjects<T>(commandType: RpcInboundCommands): Observable<DeferredData<T, any>> {
        return <Observable<DeferredData<T, any>>>this.rpcSubjects[RpcInboundCommands[commandType]];
    }

    /**
     * Connect a module with name and iframe.
     * - start pinging to iframe to wait for response.
     *
     * @param name the name of the module.
     * @param path the path to open the module the module name.
     * @param iframe the iframe window object.
     * @param primary the primary window to affect router url.
     * @return Promise<string> the promise with subName created for the window.
     */
    public moduleConnect(name: string, path: string, iframe: Window, primary: boolean): Promise<string> {
        return this.rpcManager.connectRpcOutbound(name, path, iframe, primary);
    }

    /**
     * Init the module.
     *
     * @param name the name of module.
     * @param subName the sub name of rpc channel.
     * @param entryPointType the entry point type.
     * @return Promise<void> the promise object of init result.
     */
    public moduleInit(name: string, subName: string, entryPoint: EnvironmentModuleEntryPoint): Promise<RpcInitResult> {
        const rpc = this.rpcManager.rpcChannel.getRpc<RpcOutbound>(name, subName, RpcType.Outbound);
        const self = MsftSme.self();
        const data: RpcInitData = {
            entryPointType: entryPoint.entryPointType,
            entryPointName: entryPoint.name,
            theme: self.Resources.theme,
            assets: self.Resources.assets,
            moduleAssets: self.Resources.moduleAssets,
            locale: self.Resources.localeId,
            localeRegional: self.Resources.localeRegionalId,
            lib: self.Resources.lib,
            sessionId: self.Init.sessionId,
            modules: self.Environment.modules,
            accessibilityMode: self.Resources.accessibilityMode,
            sessionExpiration: self.Init.sessionExpiration,
            performanceProfile: MsftSme.getPerformanceProfile(),
            connectivityLevel: self.Init.connectivityLevel,
            consoleDebug: MsftSme.consoleDebug(),
            sideLoad: MsftSme.sideLoad(),
            experiments: MsftSme.experiments(),
            shellVersion: self.Init.shellVersion,
            gatewayApiVersion: self.Init.gatewayApiVersion,
            gatewayPlatform: self.Init.gatewayPlatform
        };
        return rpc.init(data).then(result => {
            const strings = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Rpc;
            // copy the version string of remote module.
            if (result && result.version) {
                rpc.version = result.version;
                Logging.log({
                    level: LogLevel.Verbose,
                    message: strings.Version.message.format(rpcVersion, name, rpc.version),
                    source: 'rpc.moduleInit'
                });
            } else {
                const message = strings.MissingVersion.message.format(name);
                Logging.log({
                    level: LogLevel.Critical,
                    message,
                    source: 'rpc.moduleInit'
                });
                throw new Error(message);
            }

            this.changeActiveState(true);
            return result;
        });
    }

    /**
     * Open the module by specifying the path and parameters.
     *
     * @param name the name of module.
     * @param subName the sub name of rpc channel.
     * @param path the open path.
     * @return Promise<RpcOpenResult> the promise object of RpcOpenResult.
     */
    public moduleOpen(name: string, subName: string, path: string): Promise<RpcOpenResult> {
        const rpc = <RpcOutbound>this.rpcManager.rpcChannel.getRpc(name, subName, RpcType.Outbound);
        return rpc.open({ path }).then(result => {
            this.changeActiveState(true);
            return result;
        });
    }

    /**
     * Activate the module to start receiving data.
     *
     * @param name the module name.
     * @param subName the sub name of rpc channel.
     * @param primary the primary window to affect router url.
     * @param url the inner url to open.
     * @return Promise<void> the promise of activation result.
     */
    public moduleActivate(name: string, subName: string, primary: boolean, url: string): Promise<void> {
        const rpc = this.rpcManager.reconnectRpcOutbound(name, subName, primary);
        return rpc.activate({ url })
            .then(data => {
                this.changeActiveState(true);
                return data;
            });
    }

    /**
     * Deactivate 2 the module to stop receiving data.
     *
     * @param name the module name.
     * @param subName the sub name of rpc channel.
     * @return Promise<void> the promise of deactivation result.
     */
    public moduleDeactivate2(name: string, subName: string): Promise<RpcDeactivateResult> {
        this.changeActiveState(false);
        const rpc = this.rpcManager.rpcChannel.getRpc<RpcOutbound>(name, subName, RpcType.Outbound);
        return rpc.deactivate2({});
    }

    /**
     * Request to shutdown the module.
     *
     * @param name the module name.
     * @param subName the sub name of rpc channel.
     * @param primary the primary window to affect router url.
     * @param force the forcible state.
     * @return Promise<RpcShutdownResult> the promise object of result.
     */
    public moduleShutdown(name: string, subName: string, primary: boolean, force: boolean): Promise<RpcShutdownResult> {
        const rpc = this.rpcManager.reconnectRpcOutbound(name, subName, primary);
        return rpc.shutdown({ force: force });
    }

    /**
     * Remove the module from the rpc channel.
     *
     * @param name the module name.
     * @param subName the sub name of rpc channel.
     */
    public moduleRemove(name: string, subName: string): void {
        this.rpcManager.removeRpcOutbound(name, subName);
    }

    /**
     * Get module version string.
     *
     * @param name the name of module.
     * @param subName the sub name of rpc channel.
     * @return string the RPC version of module.
     */
    public moduleVersion(name: string, subName: string): string {
        const rpc = <RpcOutbound>this.rpcManager.rpcChannel.getRpc(name, subName, RpcType.Outbound);
        return rpc && rpc.version;
    }

    /***************************************************************
     * Section for Module usage.
     ***************************************************************/

    /**
     * Register outbound handler. It accepts delay register in case of loading/initialization took a time for module.
     *
     * @param command the name of RPC Shell command.
     * @param handler the handler to handle Shell request.
     */
    public register(command: string, handler: (data: any) => Promise<any>): void;

    /**
     * Register outbound handler. It accepts delay register in case of loading/initialization took a time for module.
     *
     * @param command the enum ID of RPC Shell command.
     * @param handler the handler to handle Shell request.
     */
    public register(command: RpcOutboundCommands, handler: (data: any) => Promise<any>): void;
    public register(command: RpcOutboundCommands | string, handler: (data: any) => Promise<any>): void {
        if (typeof command === 'string') {
            const commandName: string = command;
            this.rpcManager.rpcInbound.register(commandName, handler);
            return;
        }

        const deferredData = <DeferredData<any, any>>this.deferredCollection[command];
        if (deferredData) {
            delete this.deferredCollection[command];
            handler(deferredData.data).then(deferredData.deferred.resolve, deferredData.deferred.reject);
            return;
        }

        if (command === RpcOutboundCommands.Init) {
            const modifiedHandler = (data: RpcInitDataInternal) => {
                // node context is used before passing request to handler.
                this.changeActiveState(true);
                const self = MsftSme.self();
                self.Init.entryPointType = data.entryPointType;
                self.Init.entryPointName = data.entryPointName;
                self.Init.sessionId = data.sessionId;
                self.Init.sessionExpiration = data.sessionExpiration;
                self.Init.performanceProfile = data.performanceProfile;
                self.Init.shellVersion = data.shellVersion;

                // default is 1.0.0 and Windows which indicate it's running legacy GW.
                self.Init.gatewayApiVersion = data.gatewayApiVersion ?? '1.0.0',
                self.Init.gatewayPlatform = data.gatewayPlatform ?? 'Windows';
                self.Environment.modules = data.modules;
                self.Init.connectivityLevel = data.connectivityLevel;
                self.Resources.lib = data.lib;
                self.Resources.moduleAssets = data.moduleAssets;

                if (!MsftSme.isNullOrUndefined(self.Resources.accessibilityMode)
                    && !MsftSme.isNullOrUndefined(data.accessibilityMode)
                    && CoreEnvironment.accessibilityManager) {
                    self.Resources.accessibilityMode = data.accessibilityMode;
                    CoreEnvironment.accessibilityManager.changeAccessibilityMode(self.Resources.accessibilityMode);
                }

                // ensure that global environment settings persist across origins
                if (!MsftSme.isNullOrUndefined(data.consoleDebug) && MsftSme.consoleDebug() !== data.consoleDebug) {
                    MsftSme.consoleDebug(data.consoleDebug);
                }

                if (!MsftSme.isNullOrUndefined(data.sideLoad)) {
                    const sideLoadKeys = Object.keys(MsftSme.sideLoad() || {});
                    const dataSideLoadKeys = Object.keys(data.sideLoad || {});

                    if (sideLoadKeys.length !== dataSideLoadKeys.length || sideLoadKeys.some(key => !!data.sideLoad[key])) {
                        MsftSme.sideLoadReset();
                        dataSideLoadKeys.forEach(k => MsftSme.sideLoad(k));
                    }
                }

                if (!MsftSme.isNullOrUndefined(data.experiments)) {
                    const experiments = MsftSme.experiments();
                    if (experiments.length !== data.experiments.length || experiments.some((v, i) => v !== data.experiments[i])) {
                        MsftSme.experiments(data.experiments);
                    }
                }

                if (CoreEnvironment.assetManager && data.theme && data.assets) {
                    CoreEnvironment.assetManager.loadAssets(data.theme, data.assets);
                }

                for (const item of data.modules) {
                    // NOTES: the next 'if' block code can be removed only if all extensions adapted newer shell. (after 1.1114.0)
                    if (!self.Init.shellVersion && item.name === 'msft.sme.shell-extensions') {
                        // look for shell version if missing from older MSI/ShellUI.
                        self.Init.shellVersion = item.version;
                    }

                    if (item.name === self.Init.moduleName) {
                        self.Environment.version = item.version || self.Environment.version;
                    }
                }

                const localeLoader = CoreEnvironment.moduleLoadLocale({ id: data.localeRegional || data.locale, neutral: data.locale });
                const passingData = <RpcInitData>{
                    locale: data.locale,
                    localeRegional: data.localeRegional,
                    sessionId: data.sessionId
                };

                // update metaTags so sessionId is properly updated in telemetry
                SmeWebTelemetry.updateFromRpcInit({ 'wac-session-id': data.sessionId, 'wac-extension-version': self.Environment.version });

                return localeLoader
                    .then(() => this.seekShell(RpcSeekMode.Create))
                    .then(() => handler(passingData))
                    .then(() => ({ version: rpcVersion }));
            };
            this.rpcManager.rpcInbound.register(RpcOutboundCommands[command], modifiedHandler);
        } else {
            this.rpcManager.rpcInbound.register(RpcOutboundCommands[command], handler);
        }
    }

    /**
     * Module report a failure.
     */
    public failed(data: any): Promise<void> {
        const rpc = this.rpcManager.rpcInbound;
        return rpc.failed(data);
    }

    /**
     * Seek shell frame.
     *
     * @param Promise<any> the promise object.
     */
    public seekShell(mode: RpcSeekMode): Promise<RpcSeekResult> {
        return this.rpcManager.seekShell(mode);
    }

    /**
     * Validate existing outbound connection and remove if it doesn't live anymore.
     *
     * @return number the count of removed outbound.
     */
    public validate(): number {
        let count = 0;
        const collection = this.rpcManager.getCurrentRpcOutbound();
        collection.forEach((rpc) => {
            if (!this.rpcManager.rpcChannel.validate(rpc)) {
                count++;
            }
        });

        return count;
    }

    /**
     * Change the active status of rpc.
     */
    public changeActiveState(state: boolean): void {
        this.stateActiveInternal = state;
        this.stateChangedInternal.next(state);
    }

    /**
     * Create auto-failed timer promise.
     *
     * @param command the outbound command type.
     * @param timeoutMs the timeout milliseconds.
     * @param data the data context.
     * @return Promise<any> the promise.
     */
    private createTimerPromise<TData, TResult>(command: RpcOutboundCommands, timeoutMs: number, data: TData): Promise<TResult> {
        const deferred = NativeQ.defer<TResult>();
        this.deferredCollection[command] = <DeferredData<TData, TResult>>{ deferred: deferred, data: data };
        setTimeout(
            () => {
                const deferredData: DeferredData<TData, TResult> = this.deferredCollection[command];
                if (deferredData) {
                    const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.RpcTimeout.message;
                    deferredData.deferred.reject(message);
                    delete this.deferredCollection[command];
                }
            },
            timeoutMs);
        return deferred.promise;
    }

    /**
    * Create promise that does not timeout.
    *
    * @param command the outbound type.
    * @param data the data context.
    * @return Promise<any> the promise.
    */
    private createPromise<TData, TResult>(command: RpcOutboundCommands, data: TData): Promise<any> {
        const deferred = NativeQ.defer<TResult>();
        this.deferredCollection[command] = <DeferredData<TData, TResult>>{ deferred: deferred, data: data };
        return deferred.promise;
    }

    /**
     * Process data pushing into next call of subject with deferred data type.
     *
     * @param command the inbound command type.
     * @param data the rpc data came from a module/iframe.
     * @return Promise the promise which receiver must settle within fixed waiting time (10 seconds)
     */
    private processNextForSubject<TData, TResult>(command: RpcInboundCommands, data: TData): Promise<TResult> {
        const subject = this.rpcSubjects[RpcInboundCommands[command]];
        if (subject.closed) {
            const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.RpcSubjectClosed.message;
            throw new Error(message.format(RpcInboundCommands[command]));
        }

        const deferredData = <DeferredData<TData, TResult>>{
            data: data,
            deferred: NativeQ.defer<TResult>()
        };
        subject.next(deferredData);
        return deferredData.deferred.promise;
    }
}
